/**
 * Helper functions for unified audit log tracking
 * Based on Gap module pattern (src/views/privacy/gap/edit/Edit.vue lines 130-138)
 */

/**
 * Normaliza un valor para comparación (convierte ObjectIds y arrays a formato consistente)
 * @param {*} value - Valor a normalizar
 * @returns {*} Valor normalizado
 */
function normalizeValueForComparison(value) {
  // Valores vacíos: null, undefined, '' se tratan como equivalentes
  if (value === null || value === undefined || value === '') return null

  // Array vacío
  if (Array.isArray(value) && value.length === 0) return null

  // String numérico: convertir a número para comparación consistente (ANTES de procesar objetos)
  if (typeof value === 'string') {
    const numValue = Number(value)
    if (!isNaN(numValue) && value.trim() !== '') {
      return numValue
    }
  }

  // ObjectId como objeto BSON o { $oid: "..." }
  if (value._bsontype === 'ObjectId') return value.toString()
  if (value.$oid) return value.$oid

  // Array de ObjectIds
  if (Array.isArray(value)) {
    const normalized = value.map(item => {
      if (!item) return null
      if (item._bsontype === 'ObjectId') return item.toString()
      if (item.$oid) return item.$oid
      if (typeof item === 'object' && item._id) {
        // Si es un objeto populado (ej: { _id: "...", name: "..." }), extraer solo el ID
        return normalizeValueForComparison(item._id)
      }
      return item
    }).filter(item => item !== null) // Eliminar nulls del array

    // Si el array quedó vacío después de filtrar, retornar null
    if (normalized.length === 0) return null

    return normalized.sort() // Ordenar arrays para comparación consistente
  }

  // Fecha: normalizar solo a YYYY-MM-DD para evitar cambios falsos por diferencia de hora
  if (value.$date) {
    const d = new Date(value.$date)
    return d.toISOString().split('T')[0] // Solo fecha, sin hora
  }
  if (value instanceof Date) {
    return value.toISOString().split('T')[0] // Solo fecha, sin hora
  }
  // String ISO (ej: "2026-09-10T15:00:00.000Z")
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/.test(value)) {
    const d = new Date(value)
    return d.toISOString().split('T')[0] // Solo fecha, sin hora
  }

  // Objeto populado con _id (ej: { _id: "...", name: "..." })
  if (typeof value === 'object' && value._id) {
    return normalizeValueForComparison(value._id)
  }

  // Objeto plano: normalizar recursivamente sus propiedades
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const normalizedObj = {}
    let hasNonNullValue = false

    Object.keys(value).sort().forEach(key => { // Ordenar keys para comparación consistente
      const normalizedValue = normalizeValueForComparison(value[key])
      if (normalizedValue !== null) {
        normalizedObj[key] = normalizedValue
        hasNonNullValue = true
      }
    })

    // Si el objeto quedó vacío después de normalizar, retornar null
    return hasNonNullValue ? normalizedObj : null
  }

  return value
}

/**
 * Detecta campos modificados con valores antiguos y nuevos
 * @param {Object} originalData - Datos originales del documento
 * @param {Object} updatedData - Datos actualizados
 * @param {Array} excludeFields - Campos adicionales a excluir del tracking
 * @returns {Object} Objeto con estructura { fieldName: { from: valor_antiguo, to: valor_nuevo } }
 */
export function detectChangedFieldsWithValues(originalData, updatedData, excludeFields = []) {
  const defaultExclude = ['_id', 'history', 'auditLog', 'updatedAt', 'createdAt', 'client_id', 'lastModifiedBy', 'lastModifiedDate', 'allFileKeys', 'newEvidenceKeys']
  const allExclude = [...defaultExclude, ...excludeFields]
  const changedFields = {}

  Object.keys(updatedData).forEach(key => {
    if (allExclude.includes(key)) return

    const originalValue = originalData?.[key]
    const updatedValue = updatedData?.[key]

    // Normalizar valores para comparación justa
    const normalizedOriginal = normalizeValueForComparison(originalValue)
    const normalizedUpdated = normalizeValueForComparison(updatedValue)

    // Deep comparison usando JSON.stringify sobre valores normalizados
    if (JSON.stringify(normalizedOriginal) !== JSON.stringify(normalizedUpdated)) {
      changedFields[key] = {
        from: normalizedOriginal !== null ? originalValue : null,
        to: normalizedUpdated !== null ? updatedValue : null,
      }
    }
  })

  return changedFields
}

/**
 * Crea entrada de auditoría con estructura estándar
 * @param {Object|String} userId - ID del usuario (puede ser ObjectId o string)
 * @param {String} userName - Nombre del usuario para display
 * @param {String} action - Acción realizada (crear, actualizar, eliminar, etc.)
 * @param {Object} changedFields - Campos modificados con valores from/to
 * @returns {Object} Entrada de auditoría lista para insertar en history array
 */
export function createAuditEntry(userId, userName, action, changedFields = {}) {
  return {
    timestamp: new Date(),
    userId,
    userName,
    action,
    changedFields,
  }
}

/**
 * Extrae nombre legible de un valor (objeto populado, ID, etc.)
 * @param {*} value - Valor del que extraer el nombre
 * @returns {String|null} Nombre legible o null si no se puede extraer
 */
function extractDisplayName(value) {
  if (!value || typeof value !== 'object') return null

  // Objeto populado con campos comunes de nombre
  if (value.name) return value.name
  if (value.title) return value.title
  if (value.username) return value.username
  if (value.email) return value.email
  if (value.note) return value.note // Para casos (improvement)
  if (value.location) return value.location // Para locations

  return null
}

/**
 * Formatea valor para display en UI (maneja ObjectIds, fechas, arrays, etc.)
 * @param {*} value - Valor a formatear
 * @returns {String} Valor formateado para mostrar al usuario
 */
export function formatFieldValue(value) {
  if (value === null || value === undefined) return '-'
  if (value === '') return '-'

  // Booleano
  if (typeof value === 'boolean') return value ? 'Sí' : 'No'

  // Objeto populado (tiene _id y nombre)
  const displayName = extractDisplayName(value)
  if (displayName) return displayName

  // ObjectId puro
  if (value.$oid) return value.$oid.substring(0, 12)
  if (value._bsontype === 'ObjectId') return value.toString().substring(0, 12)

  // Fecha
  if (value.$date) return new Date(value.$date).toLocaleDateString('es-CL', { year: 'numeric', month: '2-digit', day: '2-digit' })
  if (value instanceof Date) return value.toLocaleDateString('es-CL', { year: 'numeric', month: '2-digit', day: '2-digit' })

  // Array
  if (Array.isArray(value)) {
    if (value.length === 0) return '-'

    // Intentar extraer nombres de objetos en el array
    const names = value.map(item => {
      const name = extractDisplayName(item)
      if (name) return name
      // Si es un ID puro, mostrar versión corta
      if (item?.$oid) return item.$oid.substring(0, 8)
      if (typeof item === 'string' && item.length === 24) return item.substring(0, 8)
      return item
    }).filter(Boolean)

    if (names.length > 0) {
      // Si hay pocos items, mostrarlos todos; si hay muchos, resumir
      if (names.length <= 3) return names.join(', ')
      return `${names.slice(0, 2).join(', ')} (+${names.length - 2} más)`
    }

    return `[${value.length} elemento${value.length !== 1 ? 's' : ''}]`
  }

  // Objeto complejo sin nombre identificable
  if (typeof value === 'object') {
    const keys = Object.keys(value)
    if (keys.length === 0) return '-'

    // Para objetos pequeños (hasta 6 propiedades), mostrar todas las propiedades
    if (keys.length <= 6) {
      return keys.map(k => {
        const v = value[k]
        // Formatear recursivamente valores complejos
        if (typeof v === 'boolean') return `${k}: ${v ? 'Sí' : 'No'}`
        if (v === null || v === undefined || v === '') return `${k}: -`
        if (typeof v === 'object') return `${k}: ${JSON.stringify(v)}`
        return `${k}: ${v}`
      }).join(', ')
    }

    // Para objetos grandes, mostrar resumen
    return `[Objeto con ${keys.length} propiedades]`
  }

  // Número
  if (typeof value === 'number') return value.toString()

  // String: si parece un ObjectId (24 caracteres hex), acortar
  if (typeof value === 'string' && value.length === 24 && /^[0-9a-f]{24}$/i.test(value)) {
    return value.substring(0, 12)
  }

  // Primitivo: convertir a string
  return String(value)
}
